import Page from "@/components/layout/Page";
import { SEOHead } from "@/components/seo/SEOHead";
import { BreadcrumbSchema } from "@/components/seo/BreadcrumbSchema";

export default function CookiePolicy() {
  return (
    <Page>
      <SEOHead
        title="Cookie Policy"
        description="Samboards cookie policy - how we use cookies and similar technologies to improve your experience on our website."
        canonical="/cookie-policy"
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", url: "/" },
          { name: "Cookie Policy", url: "/cookie-policy" }
        ]}
      />

      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">
            Cookie Policy
          </h1>
          <p className="text-slate-400 mb-12">Last updated: November 2025</p>

          <div className="space-y-8 text-slate-300">
            <section>
              <h2 className="text-2xl font-semibold text-slate-100 mb-4">1. What Are Cookies?</h2>
              <p className="leading-relaxed">
                Cookies are small text files that are placed on your device when you visit our website. They help us provide you with a better experience by remembering your preferences and understanding how you use our site.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-100 mb-4">2. How We Use Cookies</h2>
              <p className="leading-relaxed mb-3">
                We use cookies for several purposes:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>To remember your shopping cart contents</li>
                <li>To keep you logged in (if applicable)</li>
                <li>To understand how visitors use our website</li>
                <li>To improve website performance and functionality</li>
                <li>To provide personalized content and recommendations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-100 mb-4">3. Types of Cookies We Use</h2>
              
              <div className="space-y-6">
                <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                  <h3 className="text-xl font-medium text-slate-200 mb-3">Strictly Necessary Cookies</h3>
                  <p className="leading-relaxed">
                    These cookies are essential for the website to function properly. They enable core functionality such as security, network management, and accessibility. You cannot opt out of these cookies.
                  </p>
                  <p className="text-sm text-slate-400 mt-2">Examples: Session cookies, authentication cookies</p>
                </div>

                <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                  <h3 className="text-xl font-medium text-slate-200 mb-3">Functional Cookies</h3>
                  <p className="leading-relaxed">
                    These cookies allow us to remember your preferences and choices (such as language or region) to provide enhanced and personalized features.
                  </p>
                  <p className="text-sm text-slate-400 mt-2">Examples: Shopping cart cookies, preference cookies</p>
                </div>

                <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                  <h3 className="text-xl font-medium text-slate-200 mb-3">Analytics Cookies</h3>
                  <p className="leading-relaxed">
                    These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our website.
                  </p>
                  <p className="text-sm text-slate-400 mt-2">Examples: Google Analytics cookies</p>
                </div>

                <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                  <h3 className="text-xl font-medium text-slate-200 mb-3">Marketing Cookies</h3>
                  <p className="leading-relaxed">
                    These cookies track your online activity to help advertisers deliver more relevant advertising or to limit how many times you see an ad.
                  </p>
                  <p className="text-sm text-slate-400 mt-2">Examples: Advertising cookies, retargeting cookies</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-100 mb-4">4. Third-Party Cookies</h2>
              <p className="leading-relaxed mb-3">
                We use third-party services that may set cookies on your device:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Stripe:</strong> For secure payment processing</li>
                <li><strong>Google Analytics:</strong> For website analytics and performance monitoring</li>
                <li><strong>Social Media Platforms:</strong> For social sharing features</li>
              </ul>
              <p className="leading-relaxed mt-3">
                These third parties have their own privacy policies and cookie policies, which we encourage you to review.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-100 mb-4">5. Cookie Duration</h2>
              <p className="leading-relaxed mb-3">
                Cookies may be:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Session Cookies:</strong> Temporary cookies that expire when you close your browser</li>
                <li><strong>Persistent Cookies:</strong> Cookies that remain on your device for a set period or until you delete them</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-100 mb-4">6. Managing Cookies</h2>
              <p className="leading-relaxed mb-3">
                You can control and manage cookies in several ways:
              </p>
              
              <h3 className="text-xl font-medium text-slate-200 mb-3 mt-4">Browser Settings</h3>
              <p className="leading-relaxed mb-3">
                Most browsers allow you to refuse or accept cookies. You can usually find these settings in the "Options" or "Preferences" menu of your browser. Here are links to cookie settings for common browsers:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Chrome: Settings → Privacy and security → Cookies</li>
                <li>Firefox: Settings → Privacy & Security → Cookies</li>
                <li>Safari: Preferences → Privacy → Cookies</li>
                <li>Edge: Settings → Cookies and site permissions</li>
              </ul>

              <h3 className="text-xl font-medium text-slate-200 mb-3 mt-6">Opt-Out Tools</h3>
              <p className="leading-relaxed mb-3">
                You can opt out of certain cookies using these tools:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Google Analytics Opt-out: <span className="text-emerald-400">https://tools.google.com/dlpage/gaoptout</span></li>
                <li>Your Online Choices: <span className="text-emerald-400">https://www.youronlinechoices.com/</span></li>
              </ul>

              <div className="mt-4 p-4 bg-amber-900/20 border border-amber-700/50 rounded-lg">
                <p className="text-amber-200 font-medium mb-2">⚠️ Important Note</p>
                <p className="text-slate-300 leading-relaxed">
                  Disabling cookies may affect the functionality of our website. Some features may not work properly if cookies are disabled, including shopping cart functionality and payment processing.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-100 mb-4">7. Web Beacons and Tracking Technologies</h2>
              <p className="leading-relaxed">
                In addition to cookies, we may use other tracking technologies such as web beacons, pixels, and local storage to collect information about your use of our website.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-100 mb-4">8. Changes to This Cookie Policy</h2>
              <p className="leading-relaxed">
                We may update this Cookie Policy from time to time to reflect changes in technology, legislation, or our business practices. We will notify you of any significant changes by posting the new policy on this page.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-100 mb-4">9. Contact Us</h2>
              <p className="leading-relaxed">
                If you have questions about our use of cookies, please contact us:
              </p>
              <div className="mt-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                <p className="font-medium">Samboards</p>
                <p>Email: samboards19@aol.com</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </Page>
  );
}
